import { AutocompleteState, BaseItem } from "@algolia/autocomplete-core";
import classnames from "classnames";
import { isEmpty } from "lodash-es";
import React, { ReactNode, useEffect } from "react";

import { KeyNames } from "@utils/enums";
import { isDefined } from "@utils/helpers";

import { AutocompleteActions } from "@components/Search/SearchAutocomplete/AutocompleteSearchBox/useAutocompleteApi.hook";

import "./autocompleteItem.scss";

type AutocompleteItemProps<TItem extends BaseItem> = {
    href?: string;
    query?: string;
    getRedirectUrl?: () => string | undefined;
    onSubmit?: () => void;
    className?: string;
    itemId?: number | null;
    autocompleteState?: AutocompleteState<TItem>;
    autocompleteActions?: AutocompleteActions;
    children: ReactNode;
};

const AutocompleteItem = <TItem extends BaseItem>({
    href,
    query,
    getRedirectUrl,
    onSubmit,
    className,
    children,
    autocompleteState,
    autocompleteActions,
    itemId,
}: AutocompleteItemProps<TItem>) => {
    const active =
        isDefined(itemId) && autocompleteState?.activeItemId === itemId;

    const composedClassName = classnames(
        "autocompleteItem",
        active && "autocompleteItem--active",
        className,
    );

    const redirectUrl = getRedirectUrl?.();

    const submitQuery = () => {
        if (redirectUrl) {
            location.assign(redirectUrl);
            return;
        }

        if (!query) return;
        autocompleteActions?.setQuery(query);
        autocompleteActions?.submit(query);
    };

    const submitHref = () => {
        if (!href) return;
        window.location.href = href;
    };

    const handleOnKeyDown = (event: KeyboardEvent) => {
        if (event.key !== KeyNames.ENTER) return;
        event.stopPropagation();
        event.preventDefault();

        if (!active) return;

        if (redirectUrl) {
            location.assign(redirectUrl);
            return;
        }

        onSubmit && onSubmit();
        submitQuery();
        submitHref();
    };

    useEffect(() => {
        document.addEventListener("keydown", handleOnKeyDown);
        return () => document.removeEventListener("keydown", handleOnKeyDown);
    }, [autocompleteState?.activeItemId]);

    return isEmpty(href) ? (
        <div className={composedClassName} onClick={submitQuery}>
            {children}
        </div>
    ) : (
        <a className={composedClassName} href={href}>
            {children}
        </a>
    );
};

export { AutocompleteItem };
